import { Link } from 'react-router-dom'
import { ArrowLeft, Image } from 'lucide-react'
import * as stylex from '@stylexjs/stylex'
import { TextToImageGallery } from '@/components/gallery/TextToImageGallery'
import { ImageGalleryItem } from '@/models/gallery'
import { TextImageToImageGallery } from '@/components/gallery/TextImageToImageGallery'
import { styles } from '@/styles/ImagePage.stylex'

// Model configuration - single source of truth
const MODEL_CONFIG = {
    name: 'Runway Gen-4 Image',
    shortName: 'Runway',
    modelKey: 'runway-image',
    creditsPerImage: 8,
    heroBgImage: 'https://cdn.aibrush.co/docs/images/models/runway-image/image-hero.png',
    providerUrl: 'https://runwayml.com/research/introducing-runway-gen-4/',
    providerName: 'RunwayML',
    pageUrl: 'https://docs.aibrush.co/images-models/runway-image',
} as const;

const textToImageGalleryItems: ImageGalleryItem[] = [
    {
        id: "runway-image-1",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-1.png",

        prompt: "a transparent vitamin design capsule with a smooth, pastel beige top stands upright on a solid white background. Inside the capsule, bees hover aruond yellow billy balls, wildflowers, white blossoms, and pink clover in a golden-hued arrangement. The lighting is warm and natural, creating a sunlit, nostalgic mood. Every detail is rendered in ultra-realistic quality, from the bees' wings to the fine textures of petals. The atmosphere feels like a preserved piece of summer"
    },
    {
        id: "runway-image-2",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-2.png",
        variant: 'featured',
        prompt: "A dark, cinematic scene of a mysterious figure in flowing black robes with a dramatic red fabric veil. The woman turns to face the viewer, intense and enigmatic, while the vibrant red cloth billows backwards in slow motion, as if caught by the wind. Hyper-detailed, dynamic motion, ultra-realistic fabric simulation, dramatic lighting, deep shadows, dark fantasy aesthetic, highly detailed textures, cinematic atmosphere"
    },
    {
        id: "runway-image-4",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-4.png",
        variant: 'tall',
        prompt: "A cat with a human body, wearing a stylish jacket and cool sunglasses, looking directly into the camera. The setting is a bustling urban New York environment with iconic skyscrapers and yellow cabs in the background."
    },
    {
        id: "runway-image-3",
        width: 1080,
        height: 1920,
        aspect_ratio: "9:16",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-3.png",
        variant: 'tall',
        prompt: "Full-length model in long earthy red dress, with thin belt, black hat and boots, leading a sleek horse; dress trailing in wind; low 35-45mm, above eye-level, side sunlight or hard key 45°, shadows clean, desert as a backdrop, decisive stride"
    },
    {
        id: "runway-image-6",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-6.png",

        prompt: "fashion shooting for Dior of girl with dog, 'Dior' text behind them, black and white mode"
    },
    {
        id: "runway-image-5",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-5.png",
        variant: 'wide',
        prompt: "Two adorable guinea pigs, one white and the other black and brown, happily camping by a crackling fire. They are roasting food on sticks, with joyful expressions, in a cute and whimsical style."
    },
    {
        id: "runway-image-7",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-7.png",

        prompt: "the rocking chair on fire stays in place as it rocks back and forth slowly"
    },
    {
        id: "runway-image-8",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-8.png",
        variant: 'featured',
        prompt: "A vibrant, realistic aerial photograph of Vienna's bustling streets in the 1800s, captured at eye-level, just above the rooftops. The morning sun, around 10 AM, casts long shadows and highlights the intricate details of the architecture and the lively street scene below. Numerous food stalls and shops line the colorful streets, teeming with people walking, interacting, and children playing. Smooth tram tracks are visible, with trams moving north. A gentle breeze blows from the east, subtly affecting the movement of pedestrians and the atmosphere of the city."
    },
    {
        id: "runway-image-9",
        width: 1080,
        height: 1920,
        aspect_ratio: "9:16",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-9.png",
        variant: 'tall',
        prompt: "a beautiful pink silk gown hanging on a pure white background slowly being dyed turning teal from the bottom"
    },
    {
        id: "runway-image-10",
        width: 1080,
        height: 1920,
        aspect_ratio: "9:16",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-10.png",
        variant: 'tall',
        prompt: "Porsche 911 GT3 RS 992 Weissach Package in Shark Blue, carbon fiber hood, roof and rear wing, magnesium center-lock wheels, aggressive aerodynamics, parked on a high mountain road overlooking the French Alps, cinematic golden hour sunset, ultra realistic photography, glossy reflections, dramatic clouds, 8k hyperdetailed, professional automotive photoshoot, depth of field, magazine cover shot"
    },
    {
        id: "runway-image-11",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-11.png",

        prompt: "Side profile photography, a model wearing a white crochet cloak with hood and layers of jewellery, laying down on top of a standing spotted horse, located in a white rocky desert. Vogue photoshoot. 4K, cinematic light."
    },
    {
        id: "runway-image-12",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-12.png",

        prompt: "Photorealistic, A dynamic, cinematic shot of a sleek white chihuahua dog wearing a knight's helmet, mounted on a gigantic, armored snail. The cat is brandishing a toothpick sword, challenging a horde of tiny, menacing garden gnomes led by a cunning badger, surreal atmosphere"
    },
    {
        id: "runway-image-13",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-13.png",

        prompt: "a street photography, fashion style, street full of passers-by, motion blur focus on a subject, side close up view, 1/25 s, f/2.8, 6400 ISO, 35mm film"
    },
];

const textImageToImageGalleryItems: ImageGalleryItem[] = [
    {
        id: "runway-image-14",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-14.png",

        prompt: "show me the character in the forest",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/runway-image/gallery-13.png"]
    },
    {
        id: "runway-image-14",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-15.png",

        prompt: "the character sits ontop the floating boulder in weight less rock, the character is a girl with long hair, wearing a white dress, and a red cloak",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/runway-image/gallery-13.png", "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-14.png"]
    },
    {
        id: "runway-image-15",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-16.png",

        prompt: "the character sits on the fire chair",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/runway-image/gallery-11.png", "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-7.png"]
    },
    {
        id: "runway-image-16",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-17.png",

        prompt: "The character wore the pink dress.",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/runway-image/gallery-16.png", "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-9.png"]
    },
    {
        id: "runway-image-17",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-18.png",

        prompt: "The character wore the pink dress, and the car in the background, and remove the Dior text",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/runway-image/gallery-6.png", "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-9.png", "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-10.png"]
    }
];

const allTextImageToImage: string[] = [
    "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-6.png",
    "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-7.png",
    "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-8.png",
    "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-9.png",
    "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-10.png",
    "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-11.png",
    "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-13.png",
    "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-14.png",
    "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-15.png",
    "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-16.png",
    "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-17.png",
    "https://cdn.aibrush.co/docs/images/models/runway-image/gallery-18.png"
];

export default function RunwayImagePage() {
    return (
        <div {...stylex.props(styles.modelPage)}>
            <Link to="/" {...stylex.props(styles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            {/* Hero Section */}
            <section {...stylex.props(styles.heroSection)}>
                <div {...stylex.props(styles.heroBackground)}>
                    <img
                        src={MODEL_CONFIG.heroBgImage}
                        {...stylex.props(styles.heroBgImage)}
                        alt={`${MODEL_CONFIG.name} hero image`}
                    />
                    <div {...stylex.props(styles.heroOverlay)}></div>
                </div>
                <div {...stylex.props(styles.heroContent)}>
                    <h2 {...stylex.props(styles.heroTitle)}>{MODEL_CONFIG.name.toUpperCase()}</h2>
                    <p {...stylex.props(styles.heroDescription)}>
                        Best for <strong>Consistent Characters</strong> Experience state-of-the-art AI image generation with <strong>Text to Image</strong> and <strong>Text & Image to Image</strong> capabilities.
                        <br />
                        Powered by <a href={MODEL_CONFIG.providerUrl} target="_blank" rel="noopener noreferrer">{MODEL_CONFIG.providerName}</a>
                    </p>
                    <div {...stylex.props(styles.heroStats)}>
                        <div {...stylex.props(styles.stat)}>
                            <Image size={20} />
                            <span>Text to Image</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Image size={20} />
                            <span>Text & Image to Image</span>
                        </div>
                    </div>
                    <div {...stylex.props(styles.creditCostBadge)}>{MODEL_CONFIG.creditsPerImage} credits per image</div>
                </div>
            </section>

            {/* Image Gallery */}
            <TextToImageGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.shortName}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerImage} credits</strong> per image.`}
                title="Text to Image Gallery"
                description={`Explore the exceptional capabilities of <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.name}</a> through these AI-generated images. <strong>Click on any image</strong> to see how it was created.`}
                items={textToImageGalleryItems} />

            {/* Text & Image to Image Gallery */}
            <TextImageToImageGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.shortName}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerImage} credits</strong> per image.`}
                title="Text & Image to Image Gallery"
                description={`Experience advanced image editing with <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.name}</a> in-context editing capabilities. This model excels at <strong>consistent character generation</strong> and can reference up to <strong>3 images simultaneously</strong> for precise control over your transformations. <strong>Click on any image</strong> to see how it was created.`}
                allImages={allTextImageToImage}
                items={textImageToImageGalleryItems} />
        </div>
    )
}
